<template>
  <v-container fluid class="pa-6">
    <v-row class="align-center mb-4">
      <v-col cols="auto">
        <h2 class="text-h5">Deployed Content</h2>
      </v-col>
      <v-col cols="auto">
        <v-btn icon :loading="loadingTable" :disabled="!selectedTable" @click="reloadTable">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- ===== Selectors ===== -->
    <v-card class="mb-6">
      <v-card-text>
        <v-row dense>
          <v-col cols="3">
            <select-project
              v-model="entityId"
              label="Project"
              dense
              outlined
            />
          </v-col>
          <v-col cols="3">
            <v-select
              v-model="environment"
              :items="environments"
              :loading="loadingEnvironments"
              :disabled="!entityId || !environments.length"
              label="Environment"
              dense
              outlined
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row>
      <!-- ===== Tables list ===== -->
      <v-col cols="3">
        <v-card>
          <v-card-title class="subtitle-1 font-weight-bold">Tables</v-card-title>
          <v-divider />
          <div v-if="loadingTables" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <v-list v-else-if="tables.length" dense nav>
            <v-list-item
              v-for="t in tables"
              :key="t.name"
              :input-value="t.name === selectedTable"
              @click="selectTable(t.name)"
            >
              <v-list-item-content>
                <v-list-item-title>{{ t.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-chip x-small outlined>{{ t.rows }}</v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <v-card-text v-else class="text-caption grey--text">
            {{ environment ? 'No tables in this environment' : 'Select a project and environment' }}
          </v-card-text>
        </v-card>
      </v-col>

      <!-- ===== Table rows ===== -->
      <v-col cols="9">
        <v-card>
          <v-card-title class="subtitle-1 font-weight-bold">
            {{ selectedTable || 'Rows' }}
            <v-chip v-if="selectedTable" x-small outlined class="ml-2">{{ pagination.total }} rows</v-chip>
          </v-card-title>
          <v-divider />
          <v-data-table
            :headers="tableHeaders"
            :items="parsedRows"
            :loading="loadingTable"
            :server-items-length="pagination.total"
            :options.sync="options"
            :footer-props="{ itemsPerPageOptions: [25, 50, 100] }"
            dense
            class="rows-table"
          >
            <template #item="{ item }">
              <tr>
                <td v-for="col in columns" :key="col" class="cell">
                  <pre v-if="isObject(item[col])" class="cell-json">{{ pretty(item[col]) }}</pre>
                  <span v-else>{{ item[col] }}</span>
                </td>
              </tr>
            </template>
            <template #no-data>
              <span class="text-caption grey--text">
                {{ selectedTable ? 'Empty table' : 'Select a table' }}
              </span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { API } from '@/utils/api'
import SelectProject from '@/components/core/SelectProject/SelectProject'

export default {
  components: { SelectProject },
  data() {
    return {
      entityId: null,
      environment: null,
      environments: [],
      tables: [],
      selectedTable: null,
      columns: [],
      rows: [],
      pagination: { page: 1, perpage: 50, pages: 0, total: 0 },
      options: { page: 1, itemsPerPage: 50 },
      loadingEnvironments: false,
      loadingTables: false,
      loadingTable: false,
    }
  },
  computed: {
    tableHeaders() {
      return this.columns.map(c => ({ text: c, value: c, sortable: false }))
    },
    parsedRows() {
      return this.rows.map(r => {
        try {
          return typeof r === 'string' ? JSON.parse(r) : r
        } catch (e) {
          return {}
        }
      })
    },
  },
  watch: {
    entityId() {
      this.resetEnvironment()
      this.loadEnvironments()
    },
    environment() {
      this.resetTable()
      this.loadTables()
    },
    options: {
      handler() {
        if (this.selectedTable) {
          this.loadTable()
        }
      },
      deep: true,
    },
  },
  methods: {
    resetEnvironment() {
      this.environment = null
      this.environments = []
      this.resetTable()
    },
    resetTable() {
      this.tables = []
      this.selectedTable = null
      this.columns = []
      this.rows = []
      this.pagination = { page: 1, perpage: 50, pages: 0, total: 0 }
    },
    loadEnvironments() {
      if (!this.entityId) {
        return
      }
      this.loadingEnvironments = true
      API.call('/cms/deployed-content/list-environments.json', {
        entity_id: this.entityId,
      }).then(r => {
        this.environments = (r.data && r.data.environments) || []
      }).catch(() => {
        this.environments = []
      }).finally(() => {
        this.loadingEnvironments = false
      })
    },
    loadTables() {
      if (!this.entityId || !this.environment) {
        return
      }
      this.loadingTables = true
      API.call('/cms/deployed-content/list-tables.json', {
        entity_id: this.entityId,
        environment: this.environment,
      }).then(r => {
        this.tables = (r.data && r.data.tables) || []
      }).catch(() => {
        this.tables = []
      }).finally(() => {
        this.loadingTables = false
      })
    },
    selectTable(name) {
      if (this.selectedTable === name) {
        return
      }
      this.selectedTable = name
      // reset to first page; if already on page 1 the watcher won't fire, so load explicitly
      if (this.options.page !== 1) {
        this.options = { ...this.options, page: 1 }
      } else {
        this.loadTable()
      }
    },
    reloadTable() {
      if (this.selectedTable) {
        this.loadTable()
      }
    },
    loadTable() {
      if (!this.entityId || !this.environment || !this.selectedTable) {
        return
      }
      this.loadingTable = true
      API.call('/cms/deployed-content/table.json', {
        entity_id: this.entityId,
        environment: this.environment,
        table: this.selectedTable,
        page: this.options.page,
        perpage: this.options.itemsPerPage,
      }).then(r => {
        const data = r.data || {}
        this.columns = data.columns || []
        this.rows = data.rows || []
        this.pagination = data.pagination || { page: 1, perpage: this.options.itemsPerPage, pages: 0, total: 0 }
      }).catch(() => {
        this.columns = []
        this.rows = []
        this.pagination = { page: 1, perpage: this.options.itemsPerPage, pages: 0, total: 0 }
      }).finally(() => {
        this.loadingTable = false
      })
    },
    isObject(val) {
      return val !== null && typeof val === 'object'
    },
    pretty(val) {
      return JSON.stringify(val, null, 2)
    },
  },
}
</script>

<style scoped>
.rows-table >>> td {
  vertical-align: top;
}
.cell {
  max-width: 360px;
  overflow-x: auto;
}
.cell-json {
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 4px 0;
  max-height: 240px;
  overflow-y: auto;
}
</style>